import React, { useState, useEffect } from 'react';
import { FiRefreshCw, FiCheckCircle, FiWifiOff, FiAlertCircle } from 'react-icons/fi';
import { WebViewMessageService } from '../integrations/webview/services/messageService';
import { WebViewMessageType } from '../integrations/webview/types';

export enum ExitState {
  NONE = 'none',
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CONNECTION_LOST = 'connectionLost',
  LOADING_ERROR = 'loadingError'
}

interface WebViewExitHandlerProps {
  exitState: ExitState;
  onRetry: () => void;
  retryCount?: number;
  maxRetries?: number;
  answerData?: unknown;
}

export const WebViewExitHandler: React.FC<WebViewExitHandlerProps> = ({
  exitState,
  onRetry,
  retryCount = 0,
  maxRetries = 3,
  answerData
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [savedLocally, setSavedLocally] = useState(false);

  useEffect(() => {
    if (exitState === ExitState.FAILED && answerData) {
      try {
        localStorage.setItem('tahta_pending_answers', JSON.stringify({
          data: answerData,
          timestamp: Date.now(),
          retryCount
        }));
        setSavedLocally(true);
      } catch (error) {
        // Failed to save answers locally
      }
    }
  }, [exitState, answerData, retryCount]);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setTimeout(() => setIsRetrying(false), 500);
    }
  };

  if (exitState === ExitState.NONE) return null;

  const renderContent = () => {
    switch (exitState) {
      case ExitState.PENDING:
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-8">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Sınav Tamamlanıyor
              </h2>
              <p className="text-gray-600">
                Cevaplarınız kaydediliyor, lütfen bekleyin...
              </p>
            </div>
          </div>
        );

      case ExitState.COMPLETED:
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-8">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
              <FiCheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Sınav Başarıyla Tamamlandı
              </h2>
              <p className="text-gray-600 mb-4">
                Tüm cevaplarınız kaydedildi.
              </p>
              <p className="text-sm text-gray-500">
                Bu pencere otomatik olarak kapanacaktır...
              </p>
            </div>
          </div>
        );

      case ExitState.FAILED:
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-8">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
              <FiAlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2 text-center">
                Bağlantı Sorunu
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Cevaplarınız gönderilemedi.
                {savedLocally && (
                  <span className="block text-sm text-green-600 mt-2">
                    ✓ Cevaplarınız güvenli bir şekilde cihazınıza kaydedildi
                  </span>
                )}
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={handleRetry}
                  disabled={isRetrying || retryCount >= maxRetries}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                    isRetrying || retryCount >= maxRetries
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {isRetrying ? (
                    <>
                      <FiRefreshCw className="animate-spin" />
                      Tekrar deneniyor...
                    </>
                  ) : (
                    <>
                      <FiRefreshCw />
                      Tekrar Dene ({retryCount}/{maxRetries})
                    </>
                  )}
                </button>
                
                {retryCount >= maxRetries && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      Deneme sınırına ulaşıldı. Cevaplarınız uygulama açıldığında 
                      otomatik olarak gönderilecektir.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case ExitState.CONNECTION_LOST:
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-8">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
              <FiWifiOff className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2 text-center">
                Bağlantı Koptu
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Uygulama ile bağlantı kesildi.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                    isRetrying
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {isRetrying ? (
                    <>
                      <FiRefreshCw className="animate-spin" />
                      Bağlanıyor...
                    </>
                  ) : (
                    <>
                      <FiRefreshCw />
                      Yeniden Bağlan
                    </>
                  )}
                </button>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    Tüm cevaplarınız güvende. Bağlantı kurulduğunda 
                    kaldığınız yerden devam edebilirsiniz.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case ExitState.LOADING_ERROR:
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-8">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
              <FiAlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2 text-center">
                Test Yüklenemedi
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Test verileri yüklenirken bir hata oluştu.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    // Send test not loaded message to return to app
                    const messageService = WebViewMessageService.getInstance();
                    messageService.send(WebViewMessageType.TEST_NOT_LOADED, {
                      error: 'Test loading failed',
                      timestamp: Date.now()
                    });
                  }}
                  className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                >
                  Uygulamaya Dön
                </button>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return renderContent();
};